import React, { useCallback, useEffect, useRef, useState } from "react";
import { Download, Upload, Database, RotateCw, ShieldCheck, PlayCircle, Trash2, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";

function fmtSize(b) {
  if (b > 1024 * 1024) return (b / 1024 / 1024).toFixed(2) + " MB";
  if (b > 1024) return (b / 1024).toFixed(1) + " KB";
  return b + " B";
}

export default function AdminBackup() {
  const fileRef = useRef(null);
  const [wipe, setWipe] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState({ items: [], schedule: "", next_run: null, retention_days: 14, directory: "" });

  const loadBackups = useCallback(async () => {
    try { const { data } = await api.get("/admin/backups"); setMeta(data); }
    catch (err) { console.error("loadBackups failed:", err); }
  }, []);
  useEffect(() => { loadBackups(); }, [loadBackups]);

  const downloadFresh = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem("dw_token");
      const r = await fetch(`${API}/admin/backup`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("Backup failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.href = url; a.download = `datasprint-backup-${ts}.json`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch { toast.error("Backup failed"); }
    finally { setBusy(false); }
  };

  const downloadOne = async (name) => {
    const token = localStorage.getItem("dw_token");
    const r = await fetch(`${API}/admin/backups/${name}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { toast.error("Download failed"); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  };

  const runNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/backups/run-now");
      toast.success(`Backup created: ${data.name}`);
      loadBackups();
    } catch { toast.error("Run failed"); }
    finally { setBusy(false); }
  };

  const deleteBackup = async (name) => {
    if (!window.confirm(`Delete ${name}?`)) return;
    await api.delete(`/admin/backups/${name}`);
    loadBackups();
  };

  const onRestore = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (wipe && !window.confirm("WIPE existing data first? This cannot be undone.")) return;
    setBusy(true); setResult(null);
    try {
      const token = localStorage.getItem("dw_token");
      const fd = new FormData(); fd.append("file", file);
      const r = await fetch(`${API}/admin/restore?wipe=${wipe}`, {
        method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` }
      });
      const data = await r.json();
      setResult(data);
      if (data.ok) toast.success("Restore complete");
      else toast.error(data.detail || "Restore failed");
    } catch { toast.error("Restore failed"); }
    finally { if (fileRef.current) fileRef.current.value = ""; setBusy(false); }
  };

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Backup & Restore</h1>
          <p className="text-zinc-500 mt-2 text-sm max-w-2xl">Daily automatic local backup at 02:30 IST + on-demand export/import. Portable JSON keyed by UUIDs — works on any new domain or server with zero data loss.</p>
        </div>
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-semibold"><ShieldCheck size={12} /> Disaster-recovery ready</span>
      </div>

      {/* Schedule + status */}
      <div className="rounded-2xl bg-[var(--ink)] text-white p-5 sm:p-6 mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center"><Clock size={20} /></div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-400">Automatic Backups</div>
            <div className="font-display text-lg font-bold">{meta.schedule || "—"}</div>
            <div className="text-xs text-zinc-300 mt-1">Next run: <span className="font-mono">{meta.next_run ? new Date(meta.next_run).toLocaleString() : "—"}</span> · Retention {meta.retention_days} days · Storage: <span className="font-mono">{meta.directory}</span></div>
          </div>
        </div>
        <button onClick={runNow} disabled={busy} data-testid="run-now-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-[var(--ink)] text-sm font-semibold hover:bg-[var(--accent)] hover:text-white transition-colors disabled:opacity-50">
          <PlayCircle size={14} /> Run backup now
        </button>
      </div>

      {/* Local backups list */}
      <div className="rounded-2xl bg-white border border-[var(--line)] shadow-sm overflow-hidden mb-6">
        <div className="px-5 py-4 border-b border-[var(--line)] flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="font-display text-lg font-bold">Recent Local Backups</div>
            <div className="text-xs text-zinc-500 mt-0.5">{meta.items.length} file(s) on disk</div>
          </div>
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-zinc-50/95 backdrop-blur">
              <tr>
                <th className="text-left px-5 py-3 text-[11px] uppercase tracking-[0.12em] text-zinc-500 font-semibold">File</th>
                <th className="text-left px-5 py-3 text-[11px] uppercase tracking-[0.12em] text-zinc-500 font-semibold">Size</th>
                <th className="text-left px-5 py-3 text-[11px] uppercase tracking-[0.12em] text-zinc-500 font-semibold">Created</th>
                <th className="text-right px-5 py-3 text-[11px] uppercase tracking-[0.12em] text-zinc-500 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {meta.items.map((r) => (
                <tr key={r.name} className="border-t border-[var(--line)] hover:bg-zinc-50">
                  <td className="px-5 py-3 font-mono text-xs">{r.name}</td>
                  <td className="px-5 py-3 text-zinc-500 text-xs">{fmtSize(r.size)}</td>
                  <td className="px-5 py-3 text-zinc-500 text-xs">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => downloadOne(r.name)} className="p-1.5 rounded hover:bg-zinc-100" title="Download"><Download size={14} /></button>
                      <button onClick={() => deleteBackup(r.name)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Delete"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {!meta.items.length && <tr><td colSpan={4} className="px-5 py-10 text-center text-zinc-500 text-sm">No automatic backups yet. They will appear here after 02:30 IST tonight, or click <strong>Run backup now</strong>.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Manual export + import */}
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="rounded-2xl bg-white border border-[var(--line)] p-6 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-[var(--ink)] text-white flex items-center justify-center"><Database size={20} /></div>
            <div className="min-w-0">
              <div className="font-display text-xl font-bold">Export Database</div>
              <div className="text-sm text-zinc-500 mt-1">Stream a fresh JSON file with every collection: candidates, employees, pages, submissions, drafts, violations, contacts and login records.</div>
            </div>
          </div>
          <button onClick={downloadFresh} disabled={busy} data-testid="download-backup"
            className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--ink)] text-white font-semibold text-sm hover:bg-[var(--accent)] transition-colors disabled:opacity-50">
            <Download size={16} /> Export now
          </button>
        </div>

        <div className="rounded-2xl bg-white border border-[var(--line)] p-6 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-[var(--accent)] text-white flex items-center justify-center"><RotateCw size={20} /></div>
            <div className="min-w-0">
              <div className="font-display text-xl font-bold">Import / Restore</div>
              <div className="text-sm text-zinc-500 mt-1">Upload a backup JSON. Default mode upserts by id (safe merge). Enable Wipe for full disaster recovery.</div>
            </div>
          </div>
          <label className="mt-4 flex items-center gap-2 text-sm select-none">
            <input type="checkbox" checked={wipe} onChange={(e) => setWipe(e.target.checked)} data-testid="wipe-checkbox" />
            Wipe existing data before restore
          </label>
          <label className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[var(--line)] font-semibold text-sm cursor-pointer hover:bg-zinc-50">
            <Upload size={16} /> {busy ? "Restoring…" : "Upload backup JSON"}
            <input type="file" accept="application/json,.json" ref={fileRef} onChange={onRestore} data-testid="restore-file" className="hidden" disabled={busy} />
          </label>

          {result && (
            <div className="mt-5 rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-xs">
              <div className="font-semibold text-emerald-800 mb-2">Restore Summary {result.wipe && "(wiped)"}</div>
              <ul className="space-y-1 font-mono text-emerald-900">
                {Object.entries(result.restored || {}).map(([k, v]) => (<li key={k}>{k}: {v} docs</li>))}
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 rounded-2xl bg-zinc-50 border border-[var(--line)] p-5 text-xs text-zinc-600 leading-relaxed">
        <strong>Portability:</strong> Backup files are domain-independent and server-independent. Move any file to a new DataSprint instance and restore to continue every candidate exactly from where they left off (account, completed pages, submissions, results, drafts, violations, page assignments, audit logs).
      </div>
    </div>
  );
}
